/**
 * Pause menu (Esc / Tab / M): 继续 · 保存 · 读取 · 设置 · 回到标题, with a data-driven settings view
 * (volumes, mute, text speed, reduce motion, fullscreen, zoom) applied through ctx.updateSettings.
 * Pausing freezes the gameplay scene (scene.pause) and resumes it on close.
 */
import Phaser from 'phaser';
import { GAME_H, GAME_W } from '@/core/config';
import { Events } from '@/core/events';
import { SceneKeys } from '@/core/scenes/keys';
import type { Settings } from '@/core/types';
import { audio } from '@/audio/index';
import type { UiRoot } from './UiRoot';
import { MenuList } from './MenuList';
import { SlotPanel } from './SlotPanel';
import type { Toast } from './Toast';
import { uiText } from './text';
import { Css, Palette, Slices, UiDepth } from './theme';
import { SETTINGS_ROWS, adjustSetting, formatSettingValue, type SettingsRow } from './settingsRows';

type View = 'main' | 'settings' | 'save' | 'load';
const NO_PAUSE = new Set<string>([SceneKeys.TITLE, SceneKeys.CHAPTER_END, SceneKeys.BOOT, SceneKeys.PRELOAD]);

export class PauseMenu {
  private container: Phaser.GameObjects.Container | null = null;
  private view: View | null = null;
  private viewObjects: Phaser.GameObjects.GameObject[] = [];
  private menu: MenuList | null = null;
  private settings: SettingsView | null = null;
  private pausedKey: string | null = null;
  private standalone = false;
  readonly slots: SlotPanel;

  constructor(private readonly root: UiRoot, private readonly toast: Toast) {
    this.slots = new SlotPanel(root);
    // Integrator seam fix: while a cinematic/memory stage is playing, Esc belongs to the skip channel (ctx.skipHandler).
    root.onKey('ESC', () => { if (!this.isOpen() && root.ctx?.skipHandler) return; this.onMenuKey(); });
    root.onKey('TAB', (e) => { e.preventDefault?.(); this.onMenuKey(); });
    root.onKey('M', () => this.onMenuKey());
    root.onBus(Events.INPUT_MENU, (p) => { if (p.source !== 'keyboard') this.onMenuKey(); });
    // A router transition must never leave a stale pause modal (or a paused, already-stopped scene) behind.
    const onTransition = () => { if (this.slots.isOpen()) this.slots.cancel(true); if (this.isOpen()) this.close(true); };
    root.onBus(Events.SCENE_EXIT, onTransition);
    root.onBus(Events.SCENE_ENTER, onTransition);
    const scale = root.scene.scale;
    const onLeave = () => { const ctx = root.ctx; if (ctx?.settings.fullscreen) { ctx.updateSettings({ fullscreen: false }); this.settings?.refresh(); } };
    const onFail = () => { const ctx = root.ctx; if (ctx) ctx.updateSettings({ fullscreen: false }); this.settings?.refresh(); toast.show('无法进入全屏'); };
    scale.on(Phaser.Scale.Events.LEAVE_FULLSCREEN, onLeave);
    scale.on(Phaser.Scale.Events.FULLSCREEN_FAILED, onFail);
    scale.on(Phaser.Scale.Events.FULLSCREEN_UNSUPPORTED, onFail);
    root.track(() => { scale.off(Phaser.Scale.Events.LEAVE_FULLSCREEN, onLeave); scale.off(Phaser.Scale.Events.FULLSCREEN_FAILED, onFail); scale.off(Phaser.Scale.Events.FULLSCREEN_UNSUPPORTED, onFail); });
    root.track(() => this.close(true));
  }

  isOpen(): boolean { return this.container !== null; }
  currentView(): View | null { return this.view; }

  /** Plain dialogues/letter own ESC while open — pause must not stack on top of them. */
  private isDialogueOpen(): boolean {
    const host = this.root.scene as unknown as {
      dialogueBox?: { visible: boolean };
      letterPanel?: { isOpen(): boolean };
    };
    return host.dialogueBox?.visible === true || host.letterPanel?.isOpen() === true;
  }

  toggle(): void { if (this.isOpen()) this.close(); else this.open(); }

  /** Opens the pause menu over the running gameplay scene (no-op on title / chapter end). */
  open(): boolean {
    if (this.isOpen()) return true;
    if (this.isDialogueOpen()) return false;
    if (this.root.modal) return false;
    if (this.root.ctx?.router.isBusy()) return false;
    const world = this.root.worldScene();
    if (!world || NO_PAUSE.has(world.scene.key)) return false;
    this.standalone = false;
    this.pausedKey = world.scene.key;
    world.scene.pause();
    this.root.pushModal('pause');
    this.buildFrame('暂停');
    this.showView('main');
    audio().sfx('ui-open');
    return true;
  }

  /** Opens only the settings view (title screen); nothing is paused. */
  openSettings(): boolean {
    if (this.isOpen()) { this.showView('settings'); return true; }
    if (this.root.modal) return false;
    this.standalone = true;
    this.pausedKey = null;
    this.root.pushModal('settings');
    this.buildFrame('设置');
    this.showView('settings');
    audio().sfx('ui-open');
    return true;
  }

  close(silent = false): void {
    if (!this.container) return;
    this.clearView();
    this.container.destroy(true);
    this.container = null;
    this.view = null;
    this.root.popModal(this.standalone ? 'settings' : 'pause');
    this.resumeWorld();
    if (!silent) audio().sfx('ui-cancel');
  }

  private resumeWorld(): void {
    const key = this.pausedKey;
    this.pausedKey = null;
    if (!key) return;
    const mgr = this.root.scene.scene;
    if (mgr.isPaused(key)) mgr.resume(key);
  }

  private onMenuKey(): void {
    if (this.root.hasModal('slots') || this.root.hasModal('history') || this.root.hasModal('choices')) return;
    if (!this.isOpen() && this.isDialogueOpen()) return;
    if (!this.isOpen()) { this.open(); return; }
    if (this.view === 'main' || this.standalone) this.close();
    else this.showView('main');
  }

  private buildFrame(title: string): void {
    const s = this.root.scene;
    this.container = s.add.container(0, 0).setDepth(UiDepth.PAUSE);
    const scrim = s.add.rectangle(0, 0, GAME_W, GAME_H, 0x000000, 0.55).setOrigin(0).setInteractive();
    this.container.add(scrim);
    void title;
  }

  private clearView(): void {
    this.menu?.destroy(); this.menu = null;
    this.settings?.destroy(); this.settings = null;
    for (const o of this.viewObjects) o.destroy();
    this.viewObjects = [];
  }

  private showView(view: View): void {
    if (!this.container) return;
    this.clearView();
    this.view = view;
    const s = this.root.scene;
    const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => { this.container?.add(o); this.viewObjects.push(o); return o; };
    if (view === 'main') {
      const w = 260, h = 196;
      const x = (GAME_W - w) / 2, y = (GAME_H - h) / 2 - 6;
      add(s.add.nineslice(x, y, Slices.scroll.key, undefined, w, h, Slices.scroll.l, Slices.scroll.r, Slices.scroll.t, Slices.scroll.b).setOrigin(0));
      add(uiText(s, GAME_W / 2, y + 20, '暂停', { fontSize: '16px', fontStyle: 'bold', color: Css.lacquer }).setOrigin(0.5));
      add(uiText(s, GAME_W / 2, y + h - 14, '↑↓ 选择 · Enter 确定 · Esc 继续', { fontSize: '10px', color: Css.inkSoft }).setOrigin(0.5));
      this.menu = new MenuList(s, {
        x: GAME_W / 2, y: y + 48, width: w - 40, rowHeight: 26, fontSize: 15, color: Css.ink, selectedColor: Css.parchment, depth: UiDepth.PAUSE + 1,
        isBlocked: () => this.root.modal !== 'pause',
        // X / Backspace resume like every other panel (MenuList already played ui-cancel; Esc is handled above).
        onCancel: () => this.close(true),
        items: [
          { label: '继续', onSelect: () => this.close() },
          { label: '保存', onSelect: () => this.openSlots('save') },
          { label: '读取', onSelect: () => this.openSlots('load') },
          { label: '设置', onSelect: () => this.showView('settings') },
          { label: '回到标题', onSelect: () => this.toTitle() },
        ],
      });
    } else if (view === 'settings') {
      this.settings = new SettingsView(this.root, this.toast, {
        depth: UiDepth.PAUSE + 1,
        isBlocked: () => this.root.modal !== (this.standalone ? 'settings' : 'pause'),
        onBack: () => { if (this.standalone) this.close(); else this.showView('main'); },
      });
      this.viewObjects.push(this.settings.container);
    }
  }

  private async openSlots(mode: 'save' | 'load'): Promise<void> {
    const ctx = this.root.ctx;
    if (!ctx) return;
    const slot = await this.slots.open(mode, { standalone: false, depth: UiDepth.PAUSE + 5 });
    if (!slot) return;
    if (mode === 'save') {
      const ok = ctx.saveManual();
      this.toast.show(ok ? '已保存 · 存档一' : '保存失败');
    } else {
      this.close(true);
      const ok = await ctx.load(slot);
      if (!ok) this.toast.show('读取失败');
    }
  }

  private toTitle(): void {
    const ctx = this.root.ctx;
    this.close(true);
    void ctx?.toTitle();
  }
}

// ---------------------------------------------------------------------------- settings view
interface SettingsViewOptions { depth: number; isBlocked: () => boolean; onBack: () => void }

class SettingsView {
  readonly container: Phaser.GameObjects.Container;
  private rows: { row: SettingsRow; label: Phaser.GameObjects.Text; value: Phaser.GameObjects.Text; left: Phaser.GameObjects.Text; right: Phaser.GameObjects.Text; track?: Phaser.GameObjects.Rectangle; fill?: Phaser.GameObjects.Rectangle; zone: Phaser.GameObjects.Zone }[] = [];
  private bar: Phaser.GameObjects.NineSlice;
  private index = 0;
  private readonly keys: [string, (e: KeyboardEvent) => void][] = [];
  private static readonly W = 400;
  private static readonly ROW_H = 22;

  constructor(private readonly root: UiRoot, private readonly toast: Toast, private readonly opts: SettingsViewOptions) {
    const s = root.scene;
    const W = SettingsView.W, RH = SettingsView.ROW_H;
    const H = SETTINGS_ROWS.length * RH + 74;
    const x = (GAME_W - W) / 2, y = (GAME_H - H) / 2 - 6;
    this.container = s.add.container(0, 0).setDepth(opts.depth);
    this.container.add(s.add.nineslice(x, y, Slices.scroll.key, undefined, W, H, Slices.scroll.l, Slices.scroll.r, Slices.scroll.t, Slices.scroll.b).setOrigin(0));
    this.container.add(uiText(s, GAME_W / 2, y + 20, '设置', { fontSize: '16px', fontStyle: 'bold', color: Css.lacquer }).setOrigin(0.5));
    this.container.add(uiText(s, GAME_W / 2, y + H - 14, '↑↓ 选择 · ←→ 调整 · Enter 切换 · Esc 返回', { fontSize: '10px', color: Css.inkSoft }).setOrigin(0.5));
    this.bar = s.add.nineslice(GAME_W / 2, 0, Slices.bar.key, undefined, W - 32, RH - 2, Slices.bar.l, Slices.bar.r, Slices.bar.t, Slices.bar.b).setOrigin(0.5);
    this.container.add(this.bar);
    SETTINGS_ROWS.forEach((row, i) => {
      const ry = y + 44 + i * RH;
      const zone = s.add.zone(GAME_W / 2, ry, W - 32, RH - 2).setOrigin(0.5).setInteractive({ useHandCursor: true });
      const label = uiText(s, x + 28, ry, row.label, { fontSize: '14px', color: Css.ink }).setOrigin(0, 0.5);
      const left = uiText(s, x + W - 150, ry, '◀', { fontSize: '12px', color: Css.inkSoft }).setOrigin(0.5);
      const right = uiText(s, x + W - 28, ry, '▶', { fontSize: '12px', color: Css.inkSoft }).setOrigin(0.5);
      const value = uiText(s, x + W - 89, ry, '', { fontSize: '13px', color: Css.ink }).setOrigin(0.5);
      const items: Phaser.GameObjects.GameObject[] = [zone, label, left, right, value];
      let track: Phaser.GameObjects.Rectangle | undefined; let fill: Phaser.GameObjects.Rectangle | undefined;
      if (row.kind === 'slider') {
        track = s.add.rectangle(x + W - 140, ry + 0.5, 70, 5, Palette.parchmentDark, 1).setOrigin(0, 0.5);
        fill = s.add.rectangle(x + W - 140, ry + 0.5, 0, 5, Palette.lacquer, 1).setOrigin(0, 0.5);
        value.setX(x + W - 52);
        items.push(track, fill);
      }
      zone.on('pointerover', () => { if (!this.blocked()) this.select(i); });
      zone.on('pointerdown', (p: Phaser.Input.Pointer, lx: number) => {
        if (this.blocked()) return;
        this.select(i);
        if (row.kind === 'slider' && track) {
          const rel = (p.x - track.x) / track.width;
          if (rel >= -0.15 && rel <= 1.15) { this.setSlider(row, rel); return; }
        }
        const w = zone.width;
        if (row.kind === 'toggle') this.adjust(0);
        else this.adjust(lx > w * 0.72 ? 1 : lx > w * 0.45 ? -1 : 0);
      });
      this.container.add(items);
      const entry: (typeof this.rows)[number] = { row, label, value, left, right, zone };
      if (track) entry.track = track;
      if (fill) entry.fill = fill;
      this.rows.push(entry);
    });
    const kb = s.input.keyboard;
    const add = (key: string, fn: () => void) => {
      const wrapped = (_e: KeyboardEvent) => { if (!this.blocked()) fn(); };
      kb?.on(`keydown-${key}`, wrapped);
      this.keys.push([`keydown-${key}`, wrapped]);
    };
    add('UP', () => this.move(-1)); add('W', () => this.move(-1)); add('DOWN', () => this.move(1)); add('S', () => this.move(1));
    add('LEFT', () => this.adjust(-1)); add('A', () => this.adjust(-1)); add('RIGHT', () => this.adjust(1)); add('D', () => this.adjust(1));
    add('ENTER', () => this.adjust(0)); add('SPACE', () => this.adjust(0)); add('Z', () => this.adjust(0));
    add('X', () => this.opts.onBack()); add('BACKSPACE', () => this.opts.onBack());
    this.select(0);
    this.refresh();
  }

  private blocked(): boolean { return this.opts.isBlocked(); }

  private select(i: number): void {
    const n = this.rows.length;
    this.index = ((i % n) + n) % n;
    const r = this.rows[this.index];
    if (r) this.bar.setY(r.zone.y);
    this.rows.forEach((row, k) => {
      const sel = k === this.index;
      row.label.setColor(sel ? Css.parchment : Css.ink);
      row.value.setColor(sel ? Css.parchment : Css.ink);
      row.left.setColor(sel ? Css.parchment : Css.inkSoft);
      row.right.setColor(sel ? Css.parchment : Css.inkSoft);
      if (row.track) row.track.setFillStyle(sel ? Palette.lacquerDark : Palette.parchmentDark, 1);
      if (row.fill) row.fill.setFillStyle(sel ? Palette.lantern : Palette.lacquer, 1);
    });
  }

  private move(d: 1 | -1): void { audio().sfx('ui-move'); this.select(this.index + d); }

  private adjust(dir: -1 | 0 | 1): void {
    const ctx = this.root.ctx;
    const r = this.rows[this.index];
    if (!ctx || !r) return;
    const patch = adjustSetting(ctx.settings, r.row, dir);
    if (Object.keys(patch).length === 0) return;
    this.apply(patch);
  }

  private setSlider(row: SettingsRow, rel: number): void {
    const ctx = this.root.ctx;
    if (!ctx) return;
    const v = Math.round(Phaser.Math.Clamp(rel, 0, 1) * 10) / 10;
    this.apply({ [row.key]: v } as Partial<Settings>);
  }

  private apply(patch: Partial<Settings>): void {
    const ctx = this.root.ctx;
    if (!ctx) return;
    audio().sfx('ui-move');
    ctx.updateSettings(patch);
    if ('fullscreen' in patch) this.applyFullscreen(Boolean(patch.fullscreen));
    this.refresh();
  }

  private applyFullscreen(on: boolean): void {
    const scale = this.root.scene.scale;
    try {
      if (on && !scale.isFullscreen) scale.startFullscreen();
      else if (!on && scale.isFullscreen) scale.stopFullscreen();
    } catch (e) {
      console.warn('[ui] fullscreen toggle failed', e);
      this.root.ctx?.updateSettings({ fullscreen: false });
      this.toast.show('无法进入全屏');
    }
  }

  refresh(): void {
    const ctx = this.root.ctx;
    if (!ctx) return;
    for (const r of this.rows) {
      r.value.setText(formatSettingValue(ctx.settings, r.row));
      if (r.fill && r.track) r.fill.width = Math.round(r.track.width * (ctx.settings[r.row.key] as number));
    }
  }

  destroy(): void {
    const kb = this.root.scene.input.keyboard;
    for (const [k, fn] of this.keys) kb?.off(k, fn);
    this.keys.length = 0;
    this.container.destroy(true);
  }
}
